import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  hasAssistantSetupAccess,
  hasAssistantContentAccess,
  hasModelInfoAccess,
  hasNotificationsSettingsAccess,
  hasCensureAccess,
  hasAudioSettingsAccess,
  hasChatAssistantAccess,
  isAccountOwner,
} from '../../permissions';
import type { Account } from '../../api/client';
import { SubTabBar } from '../SubTabBar';
import { NotificationsSubTab } from './NotificationsSubTab';
import { ContentPage } from './compact/content/ContentPage';
import { MonetisationPage } from './compact/monetisation/MonetisationPage';
import { PersonalityPage } from './compact/personality/PersonalityPage';
import { SalesLadderPage } from './compact/salesLadder/SalesLadderPage';
import { SetupPage } from './compact/setup/SetupPage';
import { FanInfoPage } from './compact/fanInfo/FanInfoPage';
import { CensurePage } from './compact/censure/CensurePage';
import { NewFanFlowPage } from './compact/newFanFlow/NewFanFlowPage';
import { SandboxPage } from './compact/sandbox/SandboxPage';
import { AudioPage } from './compact/audio/AudioPage';
import { useVoicesLaunched } from '../../voices/access';

// Audio follows the Audio Tool's launch stage (ADR 0031); before launch it reads "Soon".
// The backend enforces the same (routes/audio.py, services/tool_access.py).

interface ChatAssistantTabProps {
  account: Account;
  onAccountUpdate: (account: Account) => void;
}

export function ChatAssistantTab({ account, onAccountUpdate }: ChatAssistantTabProps) {
  // The Account comes in as a prop: the Account page (/account/:id) and the Chat
  // assistant page (/chat-assistant?account=) both render this.
  const accountId = account.account_id;
  const { t } = useTranslation('chatAssistant');
  const [searchParams, setSearchParams] = useSearchParams();
  const { getPermissions, isSuperAdmin } = useAuth();

  const perms = getPermissions(accountId);
  const owner = isAccountOwner(perms);
  const audioLaunched = useVoicesLaunched();
  const audioAllowed = audioLaunched && (owner || hasAudioSettingsAccess(perms));

  const rawSubTab = searchParams.get('sub') || 'setup';
  const subTab =
    rawSubTab === 'regimes'
      ? 'setup'
      : rawSubTab === 'full-automation'
        ? 'tips' // Customs & VCs merged into Monetisation
        : rawSubTab === 'onboarding'
        ? 'new-fan-flow'
        : rawSubTab === 'content' ||
            rawSubTab === 'teasing-content' ||
            rawSubTab === 'life-content' ||
            rawSubTab === 'ppv-content'
          ? 'content-library'
          : rawSubTab;

  const subTabs: Array<{
    id: string;
    label: string;
    icon: string;
    visible: boolean;
    comingSoon?: boolean;
    beta?: boolean;
    divider?: boolean;
  }> = [
    { id: 'setup', label: t('subTabs.setup'), icon: 'sliders', visible: owner || hasAssistantSetupAccess(perms) },
    {
      id: 'model-info',
      label: t('subTabs.modelPersonality'),
      icon: 'user',
      visible: owner || hasModelInfoAccess(perms) || isSuperAdmin,
    },
    { id: 'notifications', label: t('subTabs.notifications'), icon: 'bell', visible: owner || hasNotificationsSettingsAccess(perms) },
    {
      id: 'content-library',
      label: t('subTabs.contentLibrary'),
      icon: 'grid',
      visible: owner || hasAssistantContentAccess(perms),
    },
    { id: 'sales-ladder', label: t('subTabs.salesLadder'), icon: 'trending-up', visible: owner || hasAssistantContentAccess(perms) },
    { id: 'tips', label: t('subTabs.tips'), icon: 'dollar-sign', visible: owner || hasAssistantSetupAccess(perms) },
    { id: 'censure', label: t('subTabs.censure'), icon: 'shield-off', visible: owner || hasCensureAccess(perms) },
    { id: 'fan-info', label: t('subTabs.fanInfo'), icon: 'user-check', visible: owner || hasAssistantSetupAccess(perms) },
    // PPV voice notes (bigdayz.new ADR 0068): Owner or audio_settings — not assistant_setup.
    {
      id: 'audio',
      label: t('subTabs.audio'),
      icon: 'mic',
      visible: owner || hasAudioSettingsAccess(perms) || isSuperAdmin,
      comingSoon: !audioAllowed,
      beta: true,
    },
    {
      id: 'new-fan-flow',
      label: t('subTabs.newFanFlow'),
      icon: 'user-plus',
      visible: owner || hasAssistantSetupAccess(perms),
    },
    // Sandbox Chats (ADR 0066): play a fan against this account's live setup.
    {
      id: 'sandbox',
      label: t('subTabs.sandbox'),
      icon: 'flask',
      visible: owner || hasChatAssistantAccess(perms) || isSuperAdmin,
      divider: true,
    },
  ];

  const visibleSubTabs = subTabs.filter((t) => t.visible);

  const handleSubTabChange = (id: string) => {
    if (subTabs.find((t) => t.id === id)?.comingSoon) return;
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, sub: id });
  };

  const isComingSoon = (id: string) => !!subTabs.find((t) => t.id === id)?.comingSoon;

  return (
    <div>
      <SubTabBar
        active={subTab}
        onChange={handleSubTabChange}
        tabs={visibleSubTabs.map((tab) => ({
          id: tab.id,
          label: tab.label,
          icon: tab.icon,
          disabled: tab.comingSoon,
          title: tab.comingSoon ? 'Coming soon' : undefined,
          divider: tab.divider,
          badge: tab.comingSoon ? (
            <span className="ml-0.5 text-[9px] font-extrabold uppercase tracking-wider border border-[var(--color-border-default)] rounded px-1 py-px">
              Soon
            </span>
          ) : tab.beta ? (
            <span className="ml-0.5 text-[9px] leading-none font-semibold uppercase tracking-[.04em] text-[var(--color-accent-primary)] bg-[var(--accent-primary-transparent)] rounded px-1 py-0.5">
              {t('setupKit.beta')}
            </span>
          ) : undefined,
        }))}
      />

      {subTab === 'setup' && (owner || hasAssistantSetupAccess(perms)) && (
        <SetupPage key={account.account_id} account={account} onAccountUpdate={onAccountUpdate} />
      )}
      {subTab === 'model-info' && (owner || hasModelInfoAccess(perms) || isSuperAdmin) && (
        <PersonalityPage key={account.account_id} account={account} onAccountUpdate={onAccountUpdate} />
      )}
      {subTab === 'new-fan-flow' && (owner || hasAssistantSetupAccess(perms)) && (
        <NewFanFlowPage key={account.account_id} account={account} onAccountUpdate={onAccountUpdate} />
      )}
      {subTab === 'notifications' && (owner || hasNotificationsSettingsAccess(perms)) && (
        <NotificationsSubTab key={account.account_id} account={account} onAccountUpdate={onAccountUpdate} />
      )}
      {subTab === 'content-library' && (owner || hasAssistantContentAccess(perms)) && (
        <ContentPage account={account} />
      )}
      {subTab === 'tips' && !isComingSoon('tips') && (owner || hasAssistantSetupAccess(perms)) && (
        <MonetisationPage account={account} />
      )}
      {subTab === 'sales-ladder' && (owner || hasAssistantContentAccess(perms)) && (
        <SalesLadderPage key={account.account_id} account={account} onAccountUpdate={onAccountUpdate} />
      )}
      {subTab === 'censure' && (owner || hasCensureAccess(perms)) && (
        <CensurePage key={account.account_id} account={account} />
      )}
      {subTab === 'sandbox' && (owner || hasChatAssistantAccess(perms) || isSuperAdmin) && (
        <SandboxPage key={account.account_id} account={account} />
      )}
      {subTab === 'audio' && audioAllowed && (
        <AudioPage key={account.account_id} account={account} />
      )}
      {subTab === 'fan-info' && (owner || hasAssistantSetupAccess(perms)) && (
        <FanInfoPage key={account.account_id} account={account} onAccountUpdate={onAccountUpdate} />
      )}
    </div>
  );
}
